/**
 * The door to the `desktop-sign-in` chunk: the sign-in window (DesktopSignInModal), 2026-09-30.
 *
 * The sign-in window opens only when someone signs in to an app on Prest0n's browser, yet it rode
 * the file every visitor downloads before prest0.ai can paint (about 10 KB compressed, the largest
 * of our own modules there after the workflow constants). It is fetched at the first idle moment
 * once someone is signed in, so it is almost always here by the time they press "sign in", and at
 * once when a sign-in is asked for before then. Its host (DesktopSignInModalHost, which stays on
 * the first download and listens for the request) holds the request until it arrives. The window
 * is loaded here and nowhere else (scripts/check-chat-first-paint.mjs, R4).
 */
import { createOnDemandModule } from '../../../utils/onDemandModule';
// A TYPE-only import: erased at build, never an edge into the chunk.
import type * as DesktopSignInModalNamespace from './DesktopSignInModal';

export type DesktopSignInModalModule = typeof DesktopSignInModalNamespace;

const surface = createOnDemandModule<DesktopSignInModalModule>(
  () => import(/* webpackChunkName: "desktop-sign-in" */ './DesktopSignInModal'),
);

/**
 * The sign-in window once it has arrived, or null.
 * @param signedIn someone is signed in, so fetch it at the first idle moment.
 * @param asked a sign-in was asked for, so fetch it now.
 */
export const useDesktopSignInModule = (signedIn: boolean, asked: boolean): DesktopSignInModalModule | null =>
  surface.useModule(signedIn || asked, asked);

/** Tests only: forget the download. */
export const resetDesktopSignInForTests = surface.resetForTests;
